import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { BigNumber } from 'bignumber.js';
import WCToastModern from '@/components/WCToastModern/WCToastModern.vue';
import ToastService from '@/modules/toasts/services/ToastService';
import ItemSummary from '@/models/entities/items/item-summary';
import OrderType from '@/models/entities/cart/order-type';
import { ENTRY_METHOD, EntryMethod } from '@/models/entities/cart/entry-method';
import useCartItemControls from '@/modules/cart/composables/useCartItemControls';
import useQuantityDisplay from '@/modules/orders/composables/useQuantityDisplay';
import { computed, inject, Ref } from 'vue';
import { configurationKey } from '@/injections';
import modalService from '@/modules/modals/services/modal.service';
import WCStoreSelectModal from '@/components/WCStoreSelectModal/WCStoreSelectModal.vue';
import analyticsService from '@/services/analytics.service';
import TrackableEventConstants from '@/constants/TrackableEventConstants';

export interface ImageBadge {
  icon: string;
  text: string;
}

export interface WeightOption {
  value: BigNumber;
  label: string;
  selected: boolean;
}

export default function useCartControls(orderType?: Ref<OrderType | null | undefined>) {
  const store = useStore();
  const { t } = useI18n();
  const { getItemLineItem, getLineItemsOfType, isEmpty } = useCartItemControls(orderType);
  const {
    isSoldByAverageWeight,
    isSoldByRandomWeight,
    isSoldByWeight,
    isAdditionalScanCode,
    getEachCountFromQuantity,
  } = useQuantityDisplay();
  const configuration = inject(configurationKey);

  const isGiftCard = computed(() => store.getters['cart/isGiftCard']);
  const getUser = computed(() => store.getters['user/getUser']);
  const isCustomerModeScan = computed<boolean>(() => store.getters['user/isCustomerModeScan']);

  // ALERT TOAST

  /**
   * Alert the user that they have reached the maximum quantity of an item that can be selected.
   */
  function alertMaximumQuantityReached(): void {
    (ToastService as any).openModern(WCToastModern, {
      props: {
        variant: 'warning',
        title: 'Inventory',
        message: 'Maximum quantity reached.',
        fullWidth: true,
      },
      timeout: 5000,
    });
  }

  /**
   * Alert the user that they cannot add a gift card item to their cart.
   */
  function alertGiftCardItem(): void {
    (ToastService as any).openModern(WCToastModern, {
      props: {
        variant: 'danger',
        title: 'Cannot Add Item to Cart',
        message: t('giftCardCartAlert'),
        fullWidth: true,
      },
      timeout: 7000,
    });
  }

  /**
   * Alert the user that they are not shopping from their home store.
   */
  async function alertNotShoppingFromHomeStore(): Promise<void> {
    try {
      await (modalService as any).open(WCStoreSelectModal);
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * Alert the user that the item failed to be added to the cart for an unknown reason.
   *
   * This should never happen in production, but can be possible in development.
   */
  function alertFailedToAddToCart(): void {
    (ToastService as any).openModern(WCToastModern, {
      props: {
        variant: 'danger',
        title: 'Error',
        message: 'Failed to add the item to your cart',
        fullWidth: true,
      },
      timeout: 10000,
    });
  }

  /**
   * Get the minimum quantity of an item that can be added to the cart.
   * @return {BigNumber}
   */
  function getMinimumQuantity(item: ItemSummary): BigNumber {
    if (isSoldByAverageWeight(item)) {
      return new BigNumber(item.averageWeight);
    }
    if (isSoldByRandomWeight(item)) {
      return new BigNumber(item.weightProfile.selectMinimum);
    }
    return new BigNumber(1);
  }

  /**
   * Get the department-set-maximum quantity of an item that can be added to the cart.
   *
   * @param {Object} item The item.
   * @return {BigNumber}
   */
  function getDepartmentMax(item: ItemSummary): BigNumber {
    return new BigNumber(item.maxQuantity);
  }

  /**
   * Get the maximum quantity of an item that can be added to the cart based on the weight profile. (Infinity if unweighted or sold by average weight)
   *
   * @param {Object} item The item.
   * @return {BigNumber}
   */
  function getSelectMax(item: ItemSummary): BigNumber {
    if (!isSoldByWeight(item) || isSoldByAverageWeight(item)) {
      return new BigNumber('Infinity');
    }
    const selectIncrement = new BigNumber(item.weightProfile.selectIncrement);
    const selectMinimum = new BigNumber(item.weightProfile.selectMinimum);
    const selectCount = new BigNumber(item.weightProfile.selectCount);
    return selectMinimum.plus(selectIncrement.multipliedBy(selectCount.minus(1)));
  }

  /**
   * Get the on-hand amount of an item, accounting for ASC quantity.
   *
   * @param {Object} item The item.
   * @returns {BigNumber}
   */
  function getOnHand(item: ItemSummary): BigNumber {
    const onHand = new BigNumber(item.maxBaseQuantity);
    return onHand;
  }

  /**
   * The maximum total base quantity of an item that can be added to the cart.
   * @return {BigNumber}
   */
  function getMaximumTotalBaseQuantity(item: ItemSummary): BigNumber {
    if (isCustomerModeScan.value) {
      return new BigNumber('Infinity');
    }

    const departmentMax = getDepartmentMax(item);
    const selectMax = getSelectMax(item);
    const onHand = getOnHand(item);

    if (item.sellOutOfStock) {
      return BigNumber.minimum(departmentMax, selectMax);
    }
    return BigNumber.minimum(departmentMax, selectMax, onHand);
  }

  /**
   * Check if an item is in stock.
   * @param {Object} item The item.
   * @return {boolean}
   */
  const isInStock = (item: ItemSummary): boolean => {
    if (item.sellOutOfStock || item.maxBaseQuantity === undefined) return true;
    return new BigNumber(item.maxBaseQuantity).isGreaterThanOrEqualTo(getMinimumQuantity(item));
  };

  /**
   * Check if an item is sold in stores only.
   * @param {Object} item The item.
   * @return {boolean}
   */
  const isInStoreOnly = (item: ItemSummary): boolean =>
    item.maxQuantity <= 0 || item.actualPrice < 0;

  /**
   * Check if an item is not available for purchase.
   * Matches legacy CartControls.js logic: discontinued+OOS, discontinued+in-store-only,
   * or the item has a minimum purchasable quantity of 0 or less (invalid weight data).
   * @param {Object} item The item.
   * @return {boolean}
   */
  const isNotAvailable = (item: ItemSummary): boolean =>
    (Boolean(item.discontinued) && (!isInStock(item) || isInStoreOnly(item))) ||
    getMinimumQuantity(item).isLessThanOrEqualTo(0);

  /**
   * Check if an item is purchasable but currently out of stock.
   * @param {Object} item The item.
   * @return {boolean}
   */
  const isOutOfStock = (item: ItemSummary): boolean =>
    !isNotAvailable(item) && !isInStock(item) && !isInStoreOnly(item);

  /**
   * Whether the discontinued ribbon should be shown for an item.
   * @param {Object} item The item.
   * @return {boolean}
   */
  const showDiscontinuedRibbon = (item: ItemSummary): boolean =>
    Boolean(item.discontinued) && isInStock(item) && !isInStoreOnly(item);

  /**
   * Get the out-of-stock image badge for an item, or null when in stock.
   * @param {Object} item The item.
   * @return {ImageBadge|null}
   */
  const getOutOfStockBadge = (item: ItemSummary): ImageBadge | null =>
    isOutOfStock(item) ? { icon: 'ban', text: t('outOfStock') } : null;

  /**
   * Get the quantity increment value, based on the type of item.
   * @return {BigNumber}
   */
  function getQuantityIncrement(item: ItemSummary): BigNumber {
    if (isSoldByAverageWeight(item)) {
      return new BigNumber(item.averageWeight);
    }
    if (isSoldByRandomWeight(item)) {
      return new BigNumber(item.weightProfile.selectIncrement);
    }
    return new BigNumber(1);
  }

  function getNumQuantityIncrements(item: ItemSummary, quantity: BigNumber): BigNumber {
    if (quantity.isZero()) {
      return new BigNumber(0);
    }
    const minimumQuantity = getMinimumQuantity(item);
    const quantityIncrement = getQuantityIncrement(item);
    if (quantityIncrement.isLessThanOrEqualTo(0)) {
      return new BigNumber(0);
    }
    return quantity
      .minus(minimumQuantity)
      .dividedBy(quantityIncrement)
      .integerValue(BigNumber.ROUND_HALF_UP)
      .plus(1);
  }

  /**
   * Get an item's next quantity after an increment.
   * @param {Object} item The item.
   * @param {BigNumber} currentQuantity `item`'s current quantity.
   * @return {BigNumber}
   */
  function getNextQuantityIncrement(item: ItemSummary, currentQuantity: BigNumber): BigNumber {
    if (currentQuantity.isZero()) {
      return getMinimumQuantity(item);
    }
    const quantityIncrement = getQuantityIncrement(item);
    return currentQuantity.plus(quantityIncrement);
  }

  /**
   * Get the rounded quantity set by the backend.
   *
   * This quantity can be slightly inaccurate, if the select increment has more decimals than the
   * weightProfile indicates, the extra precision will be rounded off.
   * @param {Object} item The item.
   * @param {string} variation `item`'s variation in the cart.
   * @return {BigNumber}
   */
  function getQuantityInCart(item: ItemSummary, variation: string | null): BigNumber {
    const lineItem = getItemLineItem(item, variation);
    return new BigNumber(lineItem ? lineItem.quantity : 0);
  }

  /**
   * Recover the unrounded quantity based on the rounded quantity in the cart.
   * @param {Object} item The item.
   * @param {string} variation `item`'s variation in the cart.
   * @return {BigNumber}
   */
  function recoverUnroundedQuantityFromCart(
    item: ItemSummary,
    variation: string | null,
  ): BigNumber {
    const quantityInCart = getQuantityInCart(item, variation);
    const numIncrements = getNumQuantityIncrements(item, quantityInCart);
    if (numIncrements.isZero()) {
      return new BigNumber(0);
    }
    const minimumQuantity = getMinimumQuantity(item);
    const quantityIncrement = getQuantityIncrement(item);
    return minimumQuantity.plus(numIncrements.minus(1).multipliedBy(quantityIncrement));
  }

  /**
   * Get a cart item's next quantity after an increment.
   * @param {Object} item The item.
   * @param {string} variation `item`'s variation in the cart.
   * @return {BigNumber}
   */
  function getNextQuantityIncrementInCart(item: ItemSummary, variation: string | null): BigNumber {
    const unroundedQuantity = recoverUnroundedQuantityFromCart(item, variation);
    return getNextQuantityIncrement(item, unroundedQuantity);
  }

  /**
   * Get the weighted quantity of the item in the cart, or 0 if unweighted.
   * @param {Object} item The item.
   * @param {string} variation `item`'s variation in the cart.
   * @return {BigNumber}
   */
  function getWeightedQuantityInCart(item: ItemSummary, variation: string | null): BigNumber {
    const lineItem = getItemLineItem(item, variation);
    return new BigNumber(lineItem ? lineItem.weightedItemQuantity ?? NaN : 0);
  }

  /**
   * Get the minimum weighted quantity of an item that can be added to the cart.
   *
   * Only items sold by weight have a useful weighted quantity.
   * @param {Object} item The item.
   * @return {BigNumber}
   */
  function getMinimumWeightedQuantity(item: ItemSummary): BigNumber {
    if (isSoldByWeight(item)) {
      return new BigNumber(1);
    }
    return new BigNumber('NaN');
  }

  /**
   * Get the weighted quantity increment value, based on the type of item.
   *
   * Only random weighted items can be incremented.
   * @param {Object} item The item.
   * @return {BigNumber}
   */
  function getWeightedQuantityIncrement(item: ItemSummary): BigNumber {
    if (!isSoldByRandomWeight(item)) {
      throw new Error(
        'Weighted quantity increment is not useful for items not sold by random weight',
      );
    }
    return new BigNumber(1);
  }

  /**
   * Get an item's next weighted quantity after an increment.
   * @param {Objec} item The item.
   * @param {BigNumber} currentWeightedQuantity `item`'s current weighted quantity.
   * @return {BigNumber}
   */
  function getNextWeightedQuantityIncrement(
    item: ItemSummary,
    currentWeightedQuantity: BigNumber,
  ): BigNumber {
    if (currentWeightedQuantity.isZero()) {
      return getMinimumWeightedQuantity(item);
    }
    const weightedQuantityIncrement = getWeightedQuantityIncrement(item);
    return currentWeightedQuantity.plus(weightedQuantityIncrement);
  }

  /**
   * Get a cart item's next weighted quantity after an increment.
   * @param {Object} item The item.
   * @param {string} variation `item`'s variation in the cart.
   * @return {BigNumber}
   */
  function getNextWeightedQuantityIncrementInCart(
    item: ItemSummary,
    variation: string | null,
  ): BigNumber {
    const weightedQuantity = getWeightedQuantityInCart(item, variation);
    return getNextWeightedQuantityIncrement(item, weightedQuantity);
  }

  function getNextQuantityIncrementWithWeightedQuantityInCart(
    item: ItemSummary,
    variation: string | null,
  ): {
    currentQuantity: BigNumber;
    nextQuantity: BigNumber;
    nextWeightedQuantity: BigNumber | null;
  } {
    const currentQuantity = recoverUnroundedQuantityFromCart(item, variation);
    const nextQuantity = getNextQuantityIncrementInCart(item, variation);
    let nextWeightedQuantity: BigNumber | null = null;
    if (isSoldByRandomWeight(item)) {
      if (currentQuantity.isZero()) {
        nextWeightedQuantity = getNextWeightedQuantityIncrementInCart(item, variation);
      } else {
        nextWeightedQuantity = getWeightedQuantityInCart(item, variation);
      }
    } else if (isSoldByAverageWeight(item)) {
      nextWeightedQuantity = getMinimumWeightedQuantity(item);
    }
    return { currentQuantity, nextQuantity, nextWeightedQuantity };
  }

  function getTotalBaseQuantity(
    item: ItemSummary,
    quantity: BigNumber,
    weightedQuantity: BigNumber,
  ): BigNumber {
    let totalBaseQuantity = quantity;
    if (isSoldByRandomWeight(item)) {
      totalBaseQuantity = totalBaseQuantity.multipliedBy(weightedQuantity);
    }
    if (isAdditionalScanCode(item)) {
      totalBaseQuantity = totalBaseQuantity.multipliedBy(item.ascQuantity);
    }
    return totalBaseQuantity;
  }

  function isQuantityValidIncrement(item: ItemSummary, quantity: BigNumber): boolean {
    const minimum = getMinimumQuantity(item);
    const quantityIncrement = getQuantityIncrement(item);
    if (quantityIncrement.isLessThanOrEqualTo(0)) {
      return false;
    }
    return quantity
      .minus(minimum)
      .dividedBy(quantityIncrement)
      .isInteger();
  }

  function isWeightedQuantityValidIncrement(
    item: ItemSummary,
    weightedQuantity: BigNumber,
  ): boolean {
    if (isSoldByAverageWeight(item)) {
      return weightedQuantity.isEqualTo(getMinimumWeightedQuantity(item));
    }
    if (!isSoldByRandomWeight(item)) {
      return true;
    }

    const minimum = getMinimumWeightedQuantity(item);
    const weightedQuantityIncrement = getWeightedQuantityIncrement(item);
    return weightedQuantity
      .minus(minimum)
      .dividedBy(weightedQuantityIncrement)
      .isInteger();
  }

  function isQuantityValidByItemLimits(
    item: ItemSummary,
    quantity: BigNumber,
    weightedQuantity: BigNumber,
  ): boolean {
    const totalBaseQuantity = getTotalBaseQuantity(item, quantity, weightedQuantity);
    if (totalBaseQuantity.isLessThan(0)) {
      return false;
    }
    if (totalBaseQuantity.isZero()) {
      return true;
    }

    const minimumQuantity = getMinimumQuantity(item);
    if (totalBaseQuantity.isLessThan(minimumQuantity)) {
      return false;
    }
    const maximumTotalBaseQuantity = getMaximumTotalBaseQuantity(item);
    if (totalBaseQuantity.isGreaterThan(maximumTotalBaseQuantity)) {
      return false;
    }

    if (!isQuantityValidIncrement(item, quantity)) {
      return false;
    }
    if (!isWeightedQuantityValidIncrement(item, weightedQuantity)) {
      return false;
    }

    return true;
  }

  /**
   * Get the pack quantity of an item, given it's quantity.
   * @param {Object} item The item.
   * @param {BigNumber} totalBaseQuantity `item`'s total base quantity.
   * @return {BigNumber}
   */
  function getRacsPackQuantity(item: ItemSummary, totalBaseQuantity: BigNumber): BigNumber {
    if (!item.quantityLimitData) {
      return new BigNumber(0);
    }
    return totalBaseQuantity.multipliedBy(item.quantityLimitData.comboPackAmount);
  }

  /**
   * Get the total quantity of the base item in the cart.
   * @see getTotalBaseQuantity
   * @param {Object} item The item.
   * @param {string} variation `item`'s variation in the cart.
   * @return {BigNumber}
   */
  function getTotalBaseQuantityInCart(item: ItemSummary, variation: string | null): BigNumber {
    const quantityInCart = recoverUnroundedQuantityFromCart(item, variation);
    const weightedQuantityInCart = getWeightedQuantityInCart(item, variation);
    return getTotalBaseQuantity(item, quantityInCart, weightedQuantityInCart);
  }

  /**
   * Get the pack quantity of an item in the cart.
   * @param {Object} item The item.
   * @param {string} variation `item`'s variation in the cart.
   * @return {BigNumber}
   */
  function getRacsPackQuantityInCart(item: ItemSummary, variation: string | null): BigNumber {
    return getRacsPackQuantity(item, getTotalBaseQuantityInCart(item, variation));
  }

  /**
   * The total quantity of all items in the cart of a given vendor group,
   * accounting for each item's pack quantity.
   * @param {Object} item The item whose vendor group to get the quantity of.
   * @return {BigNumber}
   */
  function getTotalQuantityForVendorGroup(item: ItemSummary): BigNumber {
    const lineItems = getLineItemsOfType(1);
    const matchVendorGroup = item.quantityLimitData.vendorGroup;
    const lineItemsInVendorGroup = lineItems.filter(
      li => li.item.quantityLimitData?.vendorGroup === matchVendorGroup,
    );
    const totalQuantityForVendorGroup = lineItemsInVendorGroup.reduce(
      (total: BigNumber, li) => total.plus(getRacsPackQuantityInCart(li.item, li.variation)),
      new BigNumber(0),
    );
    return totalQuantityForVendorGroup;
  }

  function isQuantityValidByRacs(
    item: ItemSummary,
    variation: string | null,
    totalBaseQuantity: BigNumber,
  ): boolean {
    const quantityLimit = item.quantityLimitData?.quantityLimit;
    if (!quantityLimit) {
      return true;
    }
    const currentTotalQuantityForVendorGroup = getTotalQuantityForVendorGroup(item);
    const currentRacsPackQuantityInCart = getRacsPackQuantityInCart(item, variation);
    const candidateRacsPackQuantity = getRacsPackQuantity(item, totalBaseQuantity);
    const candidateTotalQuantityForVendorGroup = currentTotalQuantityForVendorGroup
      .minus(currentRacsPackQuantityInCart)
      .plus(candidateRacsPackQuantity);
    return candidateTotalQuantityForVendorGroup.isLessThanOrEqualTo(quantityLimit);
  }

  function isQuantityValidForCart(
    item: ItemSummary,
    variation: string | null,
    quantity: BigNumber,
    weightedQuantity: BigNumber,
  ): boolean {
    const totalBaseQuantity = getTotalBaseQuantity(item, quantity, weightedQuantity);
    const validByItemLimits = isQuantityValidByItemLimits(item, quantity, weightedQuantity);
    const validByRacs = isQuantityValidByRacs(item, variation, totalBaseQuantity);
    return validByItemLimits && validByRacs;
  }

  /**
   * Check if the next quantity increment of an item in the cart is allowed.
   * @see isQuantityValidForCart
   * @param {Object} item The item.
   * @param {string} variation `item`'s variation in the cart.
   * @return {boolean}
   */
  function isNextQuantityIncrementInCartValid(
    item: ItemSummary,
    variation: string | null,
  ): boolean {
    const {
      nextQuantity,
      nextWeightedQuantity,
    } = getNextQuantityIncrementWithWeightedQuantityInCart(item, variation);
    return isQuantityValidForCart(
      item,
      variation,
      nextQuantity,
      nextWeightedQuantity ?? new BigNumber(NaN),
    );
  }

  /**
   * Get the display quantity for an item sold by random weight, roundng to the profile's decimal
   * places and including the units.
   * @param {Object} item The item.
   * @param {BigNumber} quantity The quantity of `item`.
   * @return {string}
   */
  function getDisplayRandomWeightFromQuantity(
    item: ItemSummary,
    quantity: BigNumber,
    showAbbr = false,
  ): string {
    if (!isSoldByRandomWeight(item)) {
      throw new Error(
        'Display Weighted Quantity is not defined for items not sold by random weight',
      );
    }
    const decimals = item.weightProfile.decimals;
    const roundedQuantity = quantity.toFixed(decimals, BigNumber.ROUND_HALF_UP);
    const abbrv = item.weightProfile.abbrv;
    return showAbbr ? `${roundedQuantity} ${abbrv}` : roundedQuantity;
  }

  /**
   * Get the quantity of an item to display in the UI, based on the type of item.
   * @param {Object} item The item.
   * @param {BigNumber} quantity The quantity to display.
   * @return {string}
   */
  function getDisplayQuantity(item: ItemSummary, quantity: BigNumber, showAbbr = false): string {
    if (isSoldByAverageWeight(item)) {
      return getEachCountFromQuantity(item, quantity).toString();
    }
    if (isSoldByRandomWeight(item)) {
      return getDisplayRandomWeightFromQuantity(item, quantity, showAbbr).toString();
    }
    return quantity.toString();
  }

  /**
   * Get the quantity of an item in the cart to display in the UI.
   * @see getDisplayQuantity
   * @param {Object} item The item.
   * @param {string} variation `item`'s variation in the cart.
   * @return {string}
   */
  function getDisplayQuantityInCart(item: ItemSummary, variation: string | null, showAbbr = false) {
    return getDisplayQuantity(item, recoverUnroundedQuantityFromCart(item, variation), showAbbr);
  }

  function getDisplayWeightedQuantity(item: ItemSummary, quantity: BigNumber): string {
    if (!isSoldByRandomWeight(item)) {
      throw new Error('Weighted Quantity does not exist for items not sold by random weight');
    }
    return quantity.toString();
  }

  /**
   * Create a human-readable Error describing that a quantity is invalid for an item.
   * @param {Object} item The item.
   * @param {BigNumber} quantity The invalid quantity.
   * @param {BigNumber} weightedQuantity The invalid weightedQuanity.
   * @param {BigNumber} totalBaseQuantity The invalid totalBaseQuantity.
   * @return {Error}
   */
  function createQuantityError(
    item: ItemSummary,
    quantity: BigNumber,
    weightedQuantity: BigNumber,
    totalBaseQuantity: BigNumber,
  ): Error {
    const quantityDisplay = `Quantity: ${quantity.toString()} (${getDisplayQuantity(
      item,
      quantity,
    )}),`;

    let weightedQuantityDisplay = '';
    if (isSoldByRandomWeight(item)) {
      weightedQuantityDisplay = ` Weighted Quantity: ${weightedQuantity.toString()} (${getDisplayWeightedQuantity(
        item,
        weightedQuantity,
      )}),`;
    }

    const totalBaseQuantityDisplay = `Total Base Quantity: ${totalBaseQuantity.toString()},`;

    const invalidText = `is invalid for item "${item.name}" (${item.id})`;

    return new Error(
      `${quantityDisplay}${weightedQuantityDisplay} ${totalBaseQuantityDisplay} ${invalidText}`,
    );
  }

  /**
   * Check if a new quantity is the first item added to an empty cart
   * and the logged in user is not shopping at their home store.
   * @param {BigNumber} newQuantity New quantity of item just added
   * @return {boolean}
   */
  function isFirstItemInCartAtNonHomeStore(newQuantity: number): boolean {
    return (
      isEmpty.value &&
      newQuantity > 0 &&
      getUser.value?.home !== configuration?.store?.id &&
      !!(configuration as any)?.multistore &&
      !isCustomerModeScan.value
    );
  }

  /**
   * Track the new quantity added to cart with the analytics service.
   * @param {Number} newQuantity The new quantity.
   * @param {Object} item The item.
   */
  function trackAnalyticsNewQuantity(newQuantity: number, item: ItemSummary): void {
    analyticsService.track(
      newQuantity > 0
        ? TrackableEventConstants.ItemAddedToCart
        : TrackableEventConstants.ItemRemovedFromCart,
      { items: [item], customerId: getUser.value?.sub },
    );
  }

  /**
   * Set a new quantity for an item in the cart as long as it is valid.
   * @param {number|string|BigNumber} quantity New quantity for `item`.
   * @param {number|string|BigNumber|null} weightedQuantity New weighted quantity for `item`.
   * @param {Object} item The item whose quantity to modify.
   * @param {string} variation `item`'s variation in the cart.
   * @param {Object} priceEmbeddedLineItem `item`'s price embedded data, if applicable.
   * @param {boolean} showStoreSelectModal Determines if store selection should appear. Defaults to true
   * @param {EntryMethod} entryMethod The method by which this item is being modified.
   */
  async function setQuantity(
    quantity: BigNumber | number | string,
    weightedQuantity: BigNumber | number | string | null,
    item: ItemSummary,
    variation: string | null,
    priceEmbeddedLineItem?: object,
    showStoreSelectModal = true,
    entryMethod: EntryMethod = ENTRY_METHOD.DEFAULT,
  ): Promise<void> {
    let newQuantity = new BigNumber(quantity);
    // null weightedQuantity → NaN BigNumber, matching the original JS behaviour
    let newWeightedQuantity =
      weightedQuantity !== null ? new BigNumber(weightedQuantity) : new BigNumber(NaN);
    if (newQuantity.isZero() || newWeightedQuantity.isZero()) {
      newQuantity = new BigNumber(0);
      newWeightedQuantity = new BigNumber(0);
    }
    const newTotalBaseQuantity = getTotalBaseQuantity(item, newQuantity, newWeightedQuantity);
    if (!isQuantityValidForCart(item, variation, newQuantity, newWeightedQuantity)) {
      throw createQuantityError(item, newQuantity, newWeightedQuantity, newTotalBaseQuantity);
    }

    if (isGiftCard.value) {
      alertGiftCardItem();
      throw new Error('Cannot add Gift Card Item to cart');
    }

    if (isFirstItemInCartAtNonHomeStore(newQuantity.toNumber()) && showStoreSelectModal) {
      await alertNotShoppingFromHomeStore();
    }
    trackAnalyticsNewQuantity(newQuantity.toNumber(), item);

    const itemLineItem = getItemLineItem(item, variation);
    await store.dispatch('cart/setItemQuantity', {
      itemLineItem,
      item,
      variation,
      weightedItemQuantity: isSoldByWeight(item) ? newWeightedQuantity.toNumber() : null,
      quantity: newQuantity.toNumber(),
      priceEmbeddedLineItem,
      orderType: orderType?.value,
      entryMethod,
    });
  }

  /**
   * Increment the quantity of an item in the cart if possible.
   * Shows a toast if the maximum quantity is reached or the operation fails.
   * @param {Object} item The item.
   * @param {string|null} variation The variation, if any.
   */

  async function increment(
    item: ItemSummary,
    variation: string | null,
    priceEmbeddedLineItem?: object,
    showStoreSelectModal?: boolean,
    entryMethod: EntryMethod = ENTRY_METHOD.DEFAULT,
  ): Promise<void> {
    const {
      currentQuantity,
      nextQuantity,
      nextWeightedQuantity,
    } = getNextQuantityIncrementWithWeightedQuantityInCart(item, variation);

    if (
      !isQuantityValidForCart(
        item,
        variation,
        nextQuantity,
        nextWeightedQuantity ?? new BigNumber(NaN),
      )
    ) {
      alertMaximumQuantityReached();
      return;
    }

    try {
      await setQuantity(
        nextQuantity,
        nextWeightedQuantity,
        item,
        variation,
        priceEmbeddedLineItem,
        showStoreSelectModal,
        entryMethod,
      );
    } catch (error) {
      console.error(error);
      alertFailedToAddToCart();
      return;
    }

    if (!isNextQuantityIncrementInCartValid(item, variation)) {
      alertMaximumQuantityReached();
    }
  }

  /**
   * Get an item's next quantity after a decrement.
   * @param {Object} item The item.
   * @param {BigNumber} currentQuantity `item`'s current quantity.
   * @return {BigNumber}
   */
  function getNextQuantityDecrement(item: ItemSummary, currentQuantity: BigNumber): BigNumber {
    if (currentQuantity.isLessThanOrEqualTo(getMinimumQuantity(item))) {
      return new BigNumber(0);
    }
    const quantityIncrement = getQuantityIncrement(item);
    return currentQuantity.minus(quantityIncrement);
  }

  /**
   * Get a cart item's next quantity after a decrement.
   * @param {Object} item The item.
   * @param {string} variation `item`'s variation in the cart.
   * @return {BigNumber}
   */
  function getNextQuantityDecrementInCart(item: ItemSummary, variation: string | null): BigNumber {
    const unroundedQuantity = recoverUnroundedQuantityFromCart(item, variation);
    return getNextQuantityDecrement(item, unroundedQuantity);
  }

  /**
   * Decrement the quantity of an item in the cart if possible.
   * @param {Object} item The item.
   * @param {string} variation `item`'s variation.
   * @param {Object} priceEmbeddedLineItem `item`'s price embedded data, if applicable.
   */
  async function decrement(
    item: ItemSummary,
    variation: string | null,
    priceEmbeddedLineItem?: object,
  ): Promise<void> {
    const nextQuantity = getNextQuantityDecrementInCart(item, variation);
    const weightedQuantity = getWeightedQuantityInCart(item, variation);
    await setQuantity(nextQuantity, weightedQuantity, item, variation, priceEmbeddedLineItem);
  }

  /**
   * Snap a manually-entered quantity to the nearest valid quantity for an item.
   *
   * Used by the manual quantity stepper input: rounds the typed value to the nearest
   * whole increment above the minimum, then clamps the result between zero and the
   * item's maximum total base quantity.
   * @param {number} rawQuantity The quantity typed by the user.
   * @param {Object} item The item.
   * @return {BigNumber}
   */
  function getValidQuantityFromManualEntry(rawQuantity: number, item: ItemSummary): BigNumber {
    const minimumQuantity = getMinimumQuantity(item);
    const quantityIncrement = getQuantityIncrement(item);
    if (rawQuantity <= 0 || quantityIncrement.isLessThanOrEqualTo(0)) {
      return new BigNumber(0);
    }
    let numIncrements = new BigNumber(rawQuantity)
      .minus(minimumQuantity)
      .dividedBy(quantityIncrement)
      .integerValue(BigNumber.ROUND_HALF_UP);
    numIncrements = BigNumber.maximum(numIncrements, 0);
    const maximumTotalBaseQuantity = getMaximumTotalBaseQuantity(item);
    if (maximumTotalBaseQuantity.isFinite()) {
      const maximumIncrements = maximumTotalBaseQuantity
        .minus(minimumQuantity)
        .dividedBy(quantityIncrement)
        .integerValue(BigNumber.ROUND_DOWN);
      numIncrements = BigNumber.minimum(numIncrements, maximumIncrements);
    }
    return minimumQuantity.plus(numIncrements.multipliedBy(quantityIncrement));
  }

  /**
   * Whether a manually-entered quantity rounds to more than the item's maximum allowed quantity.
   *
   * Mirrors the snap math in {@link getValidQuantityFromManualEntry}, but reports whether the
   * entry would have to be clamped down (i.e. it exceeds the max) instead of returning the clamped
   * value. An entry equal to the max is not considered above it.
   * @param {number} rawQuantity The quantity typed by the user.
   * @param {Object} item The item.
   * @return {boolean}
   */
  function isManualEntryAboveMaximum(rawQuantity: number, item: ItemSummary): boolean {
    const maximumTotalBaseQuantity = getMaximumTotalBaseQuantity(item);
    const quantityIncrement = getQuantityIncrement(item);
    if (
      !maximumTotalBaseQuantity.isFinite() ||
      rawQuantity <= 0 ||
      quantityIncrement.isLessThanOrEqualTo(0)
    ) {
      return false;
    }
    const minimumQuantity = getMinimumQuantity(item);
    const requestedIncrements = BigNumber.maximum(
      new BigNumber(rawQuantity)
        .minus(minimumQuantity)
        .dividedBy(quantityIncrement)
        .integerValue(BigNumber.ROUND_HALF_UP),
      0,
    );
    const maximumIncrements = maximumTotalBaseQuantity
      .minus(minimumQuantity)
      .dividedBy(quantityIncrement)
      .integerValue(BigNumber.ROUND_DOWN);
    return requestedIncrements.isGreaterThan(maximumIncrements);
  }

  /**
   * Builds the list of weight choices shown in the weight dropdown
   * (for example "0.25 lb", "0.50 lb"). The weight already in the cart is
   * marked as selected. Returns an empty list if the item isn't sold by weight.
   * @param {Object} item The item.
   * @param {string|null} variation `item`'s variation in the cart.
   * @return {WeightOption[]}
   */
  function getWeightOptions(item: ItemSummary, variation: string | null): WeightOption[] {
    if (!isSoldByRandomWeight(item)) {
      return [];
    }
    const minimum = getMinimumQuantity(item);
    const step = getQuantityIncrement(item);
    const maximum = getSelectMax(item);
    if (step.isLessThanOrEqualTo(0) || !maximum.isFinite()) {
      return [];
    }
    const currentDisplay = getDisplayQuantity(
      item,
      recoverUnroundedQuantityFromCart(item, variation),
    );
    const options: WeightOption[] = [];
    for (let weight = minimum; weight.isLessThanOrEqualTo(maximum); weight = weight.plus(step)) {
      options.push({
        value: weight,
        label: getDisplayQuantity(item, weight, true),
        selected: getDisplayQuantity(item, weight) === currentDisplay,
      });
    }
    return options;
  }

  /**
   * Sets the chosen weight for an item in the cart.
   * A weight of 0 removes the item from the cart.
   * @param {Object} item The item.
   * @param {string|null} variation `item`'s variation in the cart.
   * @param {number|string|BigNumber} weight The target weight.
   * @param {Object} priceEmbeddedLineItem `item`'s price embedded data, if applicable.
   * @param {boolean} showStoreSelectModal Determines if store selection should appear.
   * @param {EntryMethod} entryMethod The method by which this item is being modified.
   */
  async function setWeight(
    item: ItemSummary,
    variation: string | null,
    weight: BigNumber | number | string,
    priceEmbeddedLineItem?: object,
    showStoreSelectModal = true,
    entryMethod: EntryMethod = ENTRY_METHOD.DEFAULT,
  ): Promise<void> {
    const newWeight = new BigNumber(weight);
    await setQuantity(
      newWeight,
      newWeight.isZero() ? 0 : 1,
      item,
      variation,
      priceEmbeddedLineItem,
      showStoreSelectModal,
      entryMethod,
    );
  }

  return {
    getMinimumQuantity,
    isInStock,
    isInStoreOnly,
    isNotAvailable,
    isOutOfStock,
    showDiscontinuedRibbon,
    getOutOfStockBadge,
    getQuantityInCart,
    recoverUnroundedQuantityFromCart,
    getMaximumTotalBaseQuantity,
    getDisplayQuantityInCart,
    getDisplayQuantity,
    isNextQuantityIncrementInCartValid,
    alertMaximumQuantityReached,
    increment,
    decrement,
    setQuantity,
    getValidQuantityFromManualEntry,
    isManualEntryAboveMaximum,
    getWeightOptions,
    setWeight,
  };
}
